import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { scanReset } from '../lib/actions/scanActions';
import { getBillingStatus } from '../lib/api/billingApi';
import PreservationScore from './PreservationScore';
import CategoryModal from './CategoryModal';
import GameCard from './GameCard';
import ShareCard from './ShareCard';
import EmailCapture from './EmailCapture';
import SaveList from './SaveList';
import ReviewForm from './ReviewForm';

// Prédicats de filtre de la bibliothèque, par badge.
const GRID_FILTERS = {
  all: () => true,
  delisted: (i) => i.riskType === 'delisted',
  always_online: (i) => i.riskType === 'always_online',
  server_risk: (i) => i.riskType === 'server_risk',
  savable: (i) => i.savable && i.riskType !== 'safe',
  safe: (i) => i.riskType === 'safe',
};

function CatTile({ type, value, label, onClick }) {
  return (
    <button type="button" className={`cat-tile cat-tile--${type}`} onClick={onClick}>
      <div className="cat-tile__value">{value}</div>
      <div className="cat-tile__label">{label}</div>
      <span className="cat-tile__hint">?</span>
    </button>
  );
}

function Verdict() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const verdict = useSelector((s) => s.scan.verdict);
  const steamId = useSelector((s) => s.scan.steamId);
  const [isPro, setIsPro] = useState(null); // null = en cours de chargement
  const [modal, setModal] = useState(null); // { category } (tuile) | { item } (carte) | null
  const [gridFilter, setGridFilter] = useState('all'); // filtre de la bibliothèque par badge

  // Statut Pro du compte scanné (une seule fois) -> partagé aux sections Pro.
  useEffect(() => {
    if (!steamId) {
      setIsPro(false);
      return;
    }
    let alive = true;
    getBillingStatus(steamId)
      .then(({ data }) => alive && setIsPro(!!data.isPro))
      .catch(() => alive && setIsPro(false));
    return () => {
      alive = false;
    };
  }, [steamId]);

  if (!verdict) return null;

  // Cas profil privé : état d'erreur qui ne casse pas l'expérience.
  if (verdict.profilePrivate) {
    return (
      <section className="verdict verdict--private">
        <div className="verdict__lock" aria-hidden="true">🔒</div>
        <h1 className="verdict__private-title">{t('verdict.private_title')}</h1>
        <p className="verdict__private-body">{t('verdict.private_body')}</p>
        <p className="verdict__help">{t('verdict.private_help')}</p>
        <button type="button" className="btn btn--ghost" onClick={() => dispatch(scanReset())}>
          {t('verdict.back')}
        </button>
      </section>
    );
  }

  const {
    flaggedCount,
    totalGames,
    delistedCount,
    alwaysOnlineCount,
    serverRiskCount,
    savableCount,
    safeCount,
    items,
    library,
    isDemo,
  } = verdict;

  // Toute la bibliothèque scannée (fallback sur les flaggés si le back est ancien).
  const allGames = library && library.length > 0 ? library : items;

  return (
    <section className="verdict">
      {isDemo && <div className="demo-badge">{t('verdict.demo_badge')}</div>}

      <p className="verdict__eyebrow">
        <span className="verdict__eyebrow-txt"><i>{'//'}</i> {t('verdict.eyebrow')}</span>
      </p>

      <div className="verdict__result">
        <div className="verdict__figure">
          <span className="verdict__figure-num">{flaggedCount}</span>
          <span className="verdict__figure-total">/ {totalGames}</span>
        </div>
        <PreservationScore verdict={verdict} />
        {/* Sur mobile : la phrase passe juste sous le 17/51, la note (pscore) passe dessous.
            Sur desktop : chiffre + note sur une ligne, phrase en pleine largeur en dessous. */}
        <div className="verdict__text">
          <h1 className="verdict__headline">
            {flaggedCount > 0
              ? t('verdict.headline_text')
              : t('verdict.headline_zero', { total: totalGames })}
          </h1>
          <p className="verdict__subhead">{t('verdict.subhead')}</p>
        </div>
      </div>

      <div className="verdict__cats">
        <CatTile type="delisted" value={delistedCount} label={t('verdict.cat_delisted')} onClick={() => setModal({ category: 'delisted' })} />
        <CatTile type="always_online" value={alwaysOnlineCount} label={t('verdict.cat_always_online')} onClick={() => setModal({ category: 'always_online' })} />
        <CatTile type="server_risk" value={serverRiskCount} label={t('verdict.cat_server_risk')} onClick={() => setModal({ category: 'server_risk' })} />
        <CatTile type="savable" value={savableCount} label={t('verdict.cat_savable')} onClick={() => setModal({ category: 'savable' })} />
      </div>

      <CategoryModal modal={modal} verdict={verdict} onClose={() => setModal(null)} />

      {allGames.length > 0 && (
        <>
          <h2 className="verdict__grid-title">
            {t('verdict.library_title', { total: totalGames })}
          </h2>

          {/* Filtres par badge : n'affiche que les catégories qui ont au moins un jeu. */}
          <div className="verdict__filters">
            {[
              { key: 'all', label: t('verdict.filter_all'), count: totalGames },
              { key: 'delisted', label: t('verdict.cat_delisted'), count: delistedCount },
              { key: 'always_online', label: t('verdict.cat_always_online'), count: alwaysOnlineCount },
              { key: 'server_risk', label: t('verdict.cat_server_risk'), count: serverRiskCount },
              { key: 'savable', label: t('verdict.cat_savable'), count: savableCount },
              { key: 'safe', label: t('verdict.cat_safe'), count: safeCount },
            ]
              .filter((f) => f.key === 'all' || f.count > 0)
              .map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={`verdict__filter verdict__filter--${f.key} ${gridFilter === f.key ? 'is-active' : ''}`}
                  onClick={() => setGridFilter(f.key)}
                >
                  {f.label}
                  <span className="verdict__filter-count">{f.count}</span>
                </button>
              ))}
          </div>

          <div className="verdict__grid">
            {allGames.filter(GRID_FILTERS[gridFilter] || GRID_FILTERS.all).map((i) => (
              <GameCard key={i.appId} item={i} onExplain={(g) => setModal({ item: g })} />
            ))}
          </div>
        </>
      )}

      <SaveList items={items} isPro={isPro} isDemo={isDemo} />
      <ShareCard />
      <EmailCapture isPro={isPro} savableCount={savableCount} />
      <ReviewForm steamId={steamId} isDemo={isDemo} />

      <button
        type="button"
        className="btn btn--ghost verdict__rescan"
        onClick={() => dispatch(scanReset())}
      >
        {t('verdict.rescan')}
      </button>
    </section>
  );
}

export default Verdict;
